/*
  LineGraph can graph multiple lines.

  Data is an object mapping keys to an array of objects.
  Each key is a unique line.

  Example:
  data: {
    datasetOne: [
      { x: 0, y: 10 },
      { x: 10, y: 20 },
    ]
  }
*/

import PropTypes from 'prop-types'

import D3ChartWrapper from '~/components/d3/D3ChartWrapper'

import type { TooltipParams } from '../ChartTooltip'
import { D3ChartAxisOptions, D3ChartLayoutOptions } from '../d3/D3Chart'
import D3LineGraph, { D3LineGraphData, D3LineGraphOptions } from './D3LineGraph'

interface LineGraphProps {
  className?: string
  data: D3LineGraphData
  options?: Partial<D3LineGraphOptions>
  layoutOptions: Partial<D3ChartLayoutOptions>
  axisOptions: Partial<D3ChartAxisOptions<number, number>>
  renderTooltipContents?: (
    tooltipParams: TooltipParams &
      Partial<{ key: string; timestamp: number; value: number }>,
  ) => React.ReactNode
  chartTooltipTriggerFn?: string
  tooltipPosition?: 'right' | 'center'
  // The handler to receive the click payload from the chart
  onChartClick?: (params: any) => void
  // When provided, persist and show the tooltip for the matching node
  selectedTimestamp?: number
}

const LineGraph = ({
  data,
  options,
  className,
  layoutOptions,
  axisOptions,
  renderTooltipContents,
  chartTooltipTriggerFn,
  tooltipPosition,
  onChartClick,
  selectedTimestamp,
}: LineGraphProps) => (
  <D3ChartWrapper<D3LineGraphData, number, number, D3LineGraphOptions>
    D3ChartClass={D3LineGraph}
    className={className}
    data={data}
    options={options}
    layoutOptions={layoutOptions}
    axisOptions={axisOptions}
    renderTooltipContents={renderTooltipContents}
    chartTooltipTriggerFn={chartTooltipTriggerFn}
    tooltipPosition={tooltipPosition}
    onChartClick={onChartClick}
    selectedTimestamp={selectedTimestamp}
  />
)

LineGraph.propTypes = {
  className: PropTypes.string,
  data: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.any)),
  options: PropTypes.objectOf(PropTypes.any),
  onChartClick: PropTypes.func,
  selectedTimestamp: PropTypes.number,
}

export default LineGraph
